import { useEffect, useMemo, type Dispatch, type SetStateAction } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom'
import { fetchWithAuth } from '@/lib/api'
import type { OpenTab } from '../features/document-tabs'
import type { usePerTabNavigationHistory } from '@/hooks/use-navigation-history'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { markProgrammaticNavigation } from '@/hooks/use-url-sync'
import { buildAppUrl, isUuid, workspaceIdForDocumentUrlPath, type WorkspaceRouteInfo } from '@/lib/app-routes'
import { isDocumentDeepLinkRestorePending } from '@/lib/document-deep-link-pending'
import { isWorkspaceUserHidden } from '@/lib/user-hidden-workspaces'
import { isChatConversationTabId } from './document-viewer.utils'
import { isBasecampIntegrationTabId } from '@/lib/basecamp-tab-routing'
import {
  parentContentFolderPath,
  pathIsUnderOrEqualFolder,
  tabAffectedByFolderDelete,
  type FolderDeletedDetail,
} from '@/lib/document-delete-utils'
import { StorageKeys, StorageSchemas } from '@/lib/storage'
import {
  ensureHomeTabInOpenTabs,
  isTabVisibleForActiveWorkspaces,
  pickActiveTabForWorkspaceScope,
  resolveTabWorkspaceId,
} from './resizable-layout.handlers/shared'
import type { Workspace } from '@/providers/workspace-provider'
import { setFeedbackUiContext } from '@/lib/feedback-ui-context'
import {
  resolveOpenDocumentTarget,
  type OpenDocumentEventDetail,
} from '@/lib/open-document-event'

type Nav = ReturnType<typeof usePerTabNavigationHistory>

/** Folder list reads `selectedProjectId`. Each explorer tab stores its own folder. */
export function useSyncSelectedFolderFromExplorerTab(
  activeTabId: string | null,
  openTabs: OpenTab[],
  setSelectedProjectId: (id: string | null) => void,
) {
  useEffect(() => {
    const tab = openTabs.find((t) => t.id === activeTabId)
    if (tab?.type !== 'explorer') return
    setSelectedProjectId(tab.explorerData?.folderPath ?? null)
  }, [activeTabId, openTabs, setSelectedProjectId])
}

/** Keeps `activeDocumentPath` in sync for tree / explorer highlight and API resolution. */
export function useSyncActiveDocumentPathForTree(
  activeTabId: string | null,
  openTabs: OpenTab[],
  setActiveDocumentPath: (path: string | null) => void
) {
  useEffect(() => {
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (!activeTabId || currentTab?.type === 'explorer') {
      if (currentTab?.type === 'explorer' && currentTab.explorerData?.folderPath) {
        setActiveDocumentPath(currentTab.explorerData.folderPath)
      } else {
        setActiveDocumentPath(null)
      }
      return
    }

    const fetchDocumentPath = async () => {
      const currentTabInner = openTabs.find((tab) => tab.id === activeTabId)

      if (currentTabInner?.filePath) {
        setActiveDocumentPath(currentTabInner.filePath)
        return
      }

      if (
        activeTabId === 'home' ||
        activeTabId === 'inbox' ||
        activeTabId === 'list' ||
        activeTabId === 'graph' ||
        activeTabId === 'edit-history' ||
        activeTabId === 'view:edit-history' ||
        activeTabId === 'search-view' ||
        activeTabId === 'people-index' ||
        activeTabId.startsWith('person-view-') ||
        currentTabInner?.type === 'graph-view' ||
        currentTabInner?.type === 'workspace-home' ||
        currentTabInner?.type === 'edit-history' ||
        currentTabInner?.type === 'search-view' ||
        currentTabInner?.type === 'person-view' ||
        currentTabInner?.type === 'home' ||
        currentTabInner?.type === 'inbox' ||
        currentTabInner?.type === 'flow-index' ||
        currentTabInner?.type === 'inbox-thread' ||
        activeTabId.startsWith('flow-studio:') ||
        activeTabId.startsWith('workspace-home-') ||
        activeTabId.startsWith('home-') ||
        activeTabId.startsWith('view:') ||
        isBasecampIntegrationTabId(activeTabId) ||
        activeTabId.startsWith('integration-') ||
        activeTabId.startsWith('gdrive-preview-')
      ) {
        setActiveDocumentPath(null)
        return
      }

      if (activeTabId.includes('/')) {
        setActiveDocumentPath(activeTabId)
        return
      }

      if (activeTabId.startsWith('inbox-thread-')) {
        setActiveDocumentPath(null)
        return
      }

      if (isChatConversationTabId(activeTabId) || currentTabInner?.type === 'chat-conversation') {
        setActiveDocumentPath(null)
        return
      }

      try {
        const response = await fetchWithAuth(`/api/documents/${activeTabId}`, {
          credentials: 'include',
        })
        if (response.ok) {
          const docData = await response.json()
          const extractedPath =
            docData.path || docData.filePath || docData.file_path || docData.relative_path || null
          setActiveDocumentPath(extractedPath)
        } else {
          console.warn('[TreeNav] Document fetch failed:', response.status)
          setActiveDocumentPath(null)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('[TreeNav] Failed to fetch document path:', err)
        setActiveDocumentPath(null)
      }
    }

    void fetchDocumentPath()
  }, [activeTabId, openTabs, setActiveDocumentPath])
}

/** Publish open-tab / active-doc snapshot for pilot-feedback tickets. */
export function useSyncFeedbackUiContext(
  activeTabId: string | null,
  openTabs: OpenTab[],
  activeDocumentPath: string | null,
) {
  useEffect(() => {
    const active = openTabs.find((t) => t.id === activeTabId)
    setFeedbackUiContext({
      activeTabId: activeTabId ?? null,
      activeTabTitle: active?.title ?? null,
      activeTabType: active?.type ?? null,
      activeDocumentPath: activeDocumentPath ?? active?.filePath ?? null,
      openTabs: openTabs.slice(0, 12).map((t) => ({
        id: t.id,
        title: t.title,
        type: t.type,
        filePath: t.filePath ?? t.explorerData?.folderPath ?? null,
      })),
    })
  }, [activeTabId, openTabs, activeDocumentPath])
}

/** When Explorer is active and nav history is empty, seed `tab-main` with folder or list. */
export function useInitExplorerNavIfEmpty(
  activeTabId: string | null,
  openTabs: OpenTab[],
  selectedProjectId: string | null,
  navigation: Nav
) {
  useEffect(() => {
    const activeTab = openTabs.find((tab) => tab.id === activeTabId)
    if (activeTab?.type === 'explorer') {
      const slotId = activeTab.tabSlotId || 'tab-main'
      const currentState = navigation.tabNavigationState[slotId]

      if (!currentState || currentState.history.length === 0) {
        navigation.navigate(slotId, {
          documentId: null,
          ...(selectedProjectId != null && { folderPath: selectedProjectId }),
          viewMode: 'list',
        }, "init-explorer-nav-if-empty")
      }
    }
    // selectedProjectId read inside effect but omitted from deps to avoid navigation loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId, openTabs, navigation])
}

/**
 * Keep activeTabId aligned with workspace scope: recover missing tabs and switch away
 * from tabs hidden by the workspace switcher. Persists last-active tab per workspace.
 */
export function useRecoverOrphanOrHiddenActiveTab(
  openTabs: OpenTab[],
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  workspaces: Workspace[],
  activeWorkspaceIds: string[],
  setOpenTabs?: Dispatch<SetStateAction<OpenTab[]>>,
) {
  const navigate = useNavigate()
  const location = useLocation()
  const [lastActiveByWorkspace, setLastActiveByWorkspace] = usePersistedState<
    Record<string, string>
  >({
    key: StorageKeys.DOCUMENTS_LAST_ACTIVE_TAB_BY_WORKSPACE,
    defaultValue: {},
    schema: StorageSchemas.lastActiveTabByWorkspace,
  })

  const routeWorkspaces: WorkspaceRouteInfo[] = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces],
  )

  const replaceBarWithTab = (tab: OpenTab) => {
    const url = buildAppUrl({ tab, workspaces: routeWorkspaces })
    const path = url.split('?')[0] || '/'
    if (location.pathname === path) return
    markProgrammaticNavigation()
    navigate(url, { replace: true })
  }

  useEffect(() => {
    if (!activeTabId || openTabs.length === 0) return
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (!currentTab) return
    if (!isTabVisibleForActiveWorkspaces(currentTab, workspaces, activeWorkspaceIds)) {
      return
    }
    const wsId = resolveTabWorkspaceId(currentTab, workspaces)
    if (!wsId) return
    // Inbox thread tabs are ephemeral readers — do not restore them on next visit to /
    if (currentTab.type === 'inbox-thread' || activeTabId.startsWith('inbox-thread-')) {
      return
    }
    setLastActiveByWorkspace((prev) =>
      prev[wsId] === activeTabId ? prev : { ...prev, [wsId]: activeTabId },
    )
  }, [activeTabId, openTabs, workspaces, activeWorkspaceIds, setLastActiveByWorkspace])

  useEffect(() => {
    if (openTabs.length === 0) return
    // Cold deep link: do not steal to Home while target workspace is still OFF.
    // hideOtherWorkspaces runs in url restore; this effect must not race it.
    if (isDocumentDeepLinkRestorePending()) return

    const currentTab = activeTabId
      ? openTabs.find((tab) => tab.id === activeTabId)
      : undefined
    const isOrphan = Boolean(activeTabId && !currentTab)
    const isHidden =
      Boolean(currentTab) &&
      !isTabVisibleForActiveWorkspaces(
        currentTab!,
        workspaces,
        activeWorkspaceIds,
        // Do not pass activeTabId - a resolved out-of-scope tab must be treated as
        // hidden so Only / Hide can switch away (park tabs, restore via last-active).
      )

    if (!isOrphan && !isHidden) {
      // Tab is already in scope, but the bar can still be the hidden-workspace doc.
      // Tab-to-URL defers to a document URL, so rewrite it here.
      const urlWs = workspaceIdForDocumentUrlPath(location.pathname, routeWorkspaces)
      if (urlWs && isWorkspaceUserHidden(urlWs) && currentTab) {
        replaceBarWithTab(currentTab)
      }
      return
    }

    const fallback = pickActiveTabForWorkspaceScope(
      openTabs,
      workspaces,
      activeWorkspaceIds,
      lastActiveByWorkspace,
    )
    if (!fallback) return

    const homeMissing = !openTabs.some((t) => t.id === 'home' || t.type === 'home')
    if (fallback === 'home' && homeMissing && setOpenTabs) {
      markProgrammaticNavigation()
      setOpenTabs((prev) => ensureHomeTabInOpenTabs(prev))
      if (activeTabId !== 'home') {
        setActiveTabId('home')
      }
      replaceBarWithTab({
        id: 'home',
        title: 'Home',
        type: 'home',
        isDirty: false,
        tabSlotId: 'tab-main',
      })
      return
    }

    // Orphan activeTabId=home with no Home row (stuck Untitled + stale deep-link URL).
    if (isOrphan && activeTabId === 'home' && homeMissing && setOpenTabs) {
      markProgrammaticNavigation()
      setOpenTabs((prev) => ensureHomeTabInOpenTabs(prev))
      return
    }

    if (fallback === activeTabId) return

    markProgrammaticNavigation()
    setActiveTabId(fallback)
    const fallbackTab =
      openTabs.find((tab) => tab.id === fallback) ??
      (fallback === 'home'
        ? {
            id: 'home',
            title: 'Home',
            type: 'home' as const,
            isDirty: false,
            tabSlotId: 'tab-main',
          }
        : undefined)
    if (fallbackTab) replaceBarWithTab(fallbackTab)
  }, [
    openTabs,
    activeTabId,
    activeWorkspaceIds,
    workspaces,
    lastActiveByWorkspace,
    setActiveTabId,
    setOpenTabs,
    location.pathname,
    navigate,
    routeWorkspaces,
  ])
}

export function useClearActiveDocumentIdWhenNotExplorer(
  activeTabId: string | null,
  openTabs: OpenTab[],
  setActiveDocumentIdForFolderView: (id: string | null) => void
) {
  useEffect(() => {
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (currentTab?.type !== 'explorer') {
      setActiveDocumentIdForFolderView(null)
    }
  }, [activeTabId, openTabs, setActiveDocumentIdForFolderView])
}

export function useBrowseFolderHighlightEvent(
  activeTabId: string | null,
  openTabs: OpenTab[],
  selectedProjectId: string | null
) {
  useEffect(() => {
    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (currentTab?.type === 'explorer' && selectedProjectId) {
      window.dispatchEvent(
        new CustomEvent('show-folder-in-browse', {
          detail: { folderPath: selectedProjectId },
        })
      )
    }
  }, [activeTabId, selectedProjectId, openTabs])
}

export function useShowDocumentInBrowseAndOpenDocumentEvents(
  showSidebar: boolean,
  sidebarCollapsed: boolean,
  setShowSidebar: (v: boolean) => void,
  setSidebarCollapsed: (v: boolean | ((p: boolean) => boolean)) => void,
  handleDocumentClick: (
    path: string,
    event?: ReactMouseEvent,
    options?: { searchQuery?: string },
  ) => void | Promise<void>
) {
  useEffect(() => {
    const handleShowInBrowse = (event: Event) => {
      // Opening a doc updates the tree in the background. Only an explicit
      // Reveal in sidebar (scrollToNode) unhides a collapsed sidebar.
      const scrollToNode = (event as CustomEvent<{ scrollToNode?: boolean }>).detail?.scrollToNode
      if (!scrollToNode) return
      if (!showSidebar) {
        setShowSidebar(true)
      }
      if (sidebarCollapsed) {
        setSidebarCollapsed(false)
      }
    }

    window.addEventListener('show-document-in-browse', handleShowInBrowse)

    const handleOpenDocument = async (
      event: CustomEvent<OpenDocumentEventDetail>
    ) => {
      const { openInNewTab = false, clickEvent, searchQuery } = event.detail ?? {}
      const idOrPath = resolveOpenDocumentTarget(event.detail)

      const syntheticEvent = clickEvent
        ? ({
            metaKey: clickEvent.metaKey,
            ctrlKey: clickEvent.ctrlKey,
            preventDefault: () => {},
            stopPropagation: () => {},
          } as ReactMouseEvent)
        : openInNewTab
          ? ({
              metaKey: true,
              ctrlKey: false,
              preventDefault: () => {},
              stopPropagation: () => {},
            } as ReactMouseEvent)
          : undefined

      await handleDocumentClick(
        idOrPath,
        syntheticEvent,
        searchQuery != null ? { searchQuery } : undefined,
      )
    }

    window.addEventListener('open-document', handleOpenDocument as unknown as EventListener)
    return () => {
      window.removeEventListener('show-document-in-browse', handleShowInBrowse)
      window.removeEventListener('open-document', handleOpenDocument as unknown as EventListener)
    }
  }, [
    showSidebar,
    sidebarCollapsed,
    setShowSidebar,
    setSidebarCollapsed,
    handleDocumentClick,
  ])
}

export function useDocumentPathUpdatedEvent(
  openTabs: OpenTab[],
  activeTabId: string | null,
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>,
  setActiveTabId: (id: string | null) => void,
  setActiveDocumentPath: (path: string | null) => void,
  navigation: Nav
) {
  useEffect(() => {
    const handleDocumentPathUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{
        documentId: string
        oldPath: string
        newPath: string
        title?: string
      }>

      const { documentId, oldPath, newPath, title } = customEvent.detail

      const tabToUpdate = openTabs.find(
        (tab) =>
          tab.id === oldPath ||
          tab.id === documentId ||
          tab.filePath === oldPath ||
          (tab.id.includes('/') && tab.id === oldPath)
      )

      if (tabToUpdate) {
        const updatedTabs = openTabs.map((tab) => {
          if (tab.id !== tabToUpdate.id) return tab
          return {
            ...tab,
            ...(isUuid(tab.id) ? {} : { id: newPath }),
            filePath: newPath,
            ...(title ? { title } : {}),
          }
        })
        setOpenTabs(updatedTabs)

        const activeMatches =
          activeTabId === tabToUpdate.id ||
          activeTabId === oldPath ||
          tabToUpdate.filePath === oldPath && activeTabId === tabToUpdate.id

        if (activeMatches) {
          const nextActiveId = isUuid(tabToUpdate.id) ? tabToUpdate.id : newPath
          setActiveTabId(nextActiveId)
          setActiveDocumentPath(newPath)

          const tabSlotId = tabToUpdate.tabSlotId
          if (tabSlotId) {
            markProgrammaticNavigation()
            navigation.navigate(
              tabSlotId,
              { documentId: isUuid(tabToUpdate.id) ? tabToUpdate.id : newPath, viewMode: 'single' },
              'doc-path-updated-event'
            )
          }
        } else if (newPath.includes('/')) {
          setActiveDocumentPath(newPath)
        }
      }
    }

    window.addEventListener('document-path-updated', handleDocumentPathUpdate)
    return () => {
      window.removeEventListener('document-path-updated', handleDocumentPathUpdate)
    }
  }, [openTabs, activeTabId, navigation, setOpenTabs, setActiveTabId, setActiveDocumentPath])
}

export function usePinDocumentByPathEvent(
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>
) {
  useEffect(() => {
    const handler = (e: CustomEvent<{ path: string }>) => {
      const { path } = e.detail
      setTimeout(() => {
        setOpenTabs((prev) =>
          prev.map((tab) =>
            (tab.filePath === path || tab.id === path) && !tab.isPinned
              ? { ...tab, isPinned: true }
              : tab
          )
        )
      }, 100)
    }
    window.addEventListener('pin-document-by-path', handler as EventListener)
    return () => window.removeEventListener('pin-document-by-path', handler as EventListener)
  }, [setOpenTabs])
}

export function usePinTabByIdEvent(
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>
) {
  useEffect(() => {
    const handler = (e: CustomEvent<{ tabId: string }>) => {
      const { tabId } = e.detail
      if (!tabId) return
      setTimeout(() => {
        setOpenTabs((prev) =>
          prev.map((tab) =>
            tab.id === tabId && !tab.isPinned ? { ...tab, isPinned: true } : tab,
          ),
        )
      }, 100)
    }
    window.addEventListener('pin-tab-by-id', handler as EventListener)
    return () => window.removeEventListener('pin-tab-by-id', handler as EventListener)
  }, [setOpenTabs])
}

export function useFlownotesFocusViewEvent(
  setSidebarCollapsed: (v: boolean | ((p: boolean) => boolean)) => void,
  setAgentCollapsed: (v: boolean | ((p: boolean) => boolean)) => void,
  /** Call before collapsing chat so UI_EXPANDED_RIGHT_SIZE / panel snapshot stay valid. */
  persistChatPanelWidthBeforeCollapse?: () => void
) {
  useEffect(() => {
    const onFocusView = () => {
      setSidebarCollapsed(true)
      persistChatPanelWidthBeforeCollapse?.()
      setAgentCollapsed(true)
    }
    window.addEventListener('flownotes-focus-view', onFocusView)
    return () => window.removeEventListener('flownotes-focus-view', onFocusView)
  }, [setSidebarCollapsed, setAgentCollapsed, persistChatPanelWidthBeforeCollapse])
}

/** Folder / explorer toolbars dispatch this to mirror the header left-sidebar toggle. */
export function useFlownotesToggleLeftSidebarEvent(toggleSidebarCollapsed: () => void) {
  useEffect(() => {
    const onToggle = () => toggleSidebarCollapsed()
    window.addEventListener('flownotes-toggle-left-sidebar', onToggle)
    return () => window.removeEventListener('flownotes-toggle-left-sidebar', onToggle)
  }, [toggleSidebarCollapsed])
}

/** Collapse right agent panel when chat moves to editor tab (avoid duplicate welcome UI). */
export function useChatAgentPanelCollapseEvent(
  setAgentCollapsed: (v: boolean | ((p: boolean) => boolean)) => void,
  persistChatPanelWidthBeforeCollapse?: () => void,
) {
  useEffect(() => {
    const onCollapse = () => {
      persistChatPanelWidthBeforeCollapse?.()
      setAgentCollapsed(true)
    }
    window.addEventListener('chat-agent-panel-collapse', onCollapse)
    return () => window.removeEventListener('chat-agent-panel-collapse', onCollapse)
  }, [setAgentCollapsed, persistChatPanelWidthBeforeCollapse])
}

function urlForOpenTab(tab: OpenTab, workspacesRoute: WorkspaceRouteInfo[]): string {
  return buildAppUrl({ tab, workspaces: workspacesRoute })
}

/** Close explorer/document tabs rooted in a deleted folder; navigate away from stale folder views. */
export function useFolderDeletedTabCleanup(
  openTabs: OpenTab[],
  activeTabId: string | null,
  setOpenTabs: Dispatch<SetStateAction<OpenTab[]>>,
  setActiveTabId: (id: string | null) => void,
  selectedProjectId: string | null,
  setSelectedProjectId: (id: string | null) => void,
  navigate: NavigateFunction,
  workspacesRoute: WorkspaceRouteInfo[],
) {
  useEffect(() => {
    const handler = (event: CustomEvent<FolderDeletedDetail>) => {
      const deletedPath = event.detail?.path
      if (!deletedPath) return

      const remaining = openTabs.filter((tab) => !tabAffectedByFolderDelete(tab, deletedPath))
      const activeWasRemoved = Boolean(
        activeTabId && openTabs.some((t) => t.id === activeTabId && tabAffectedByFolderDelete(t, deletedPath)),
      )
      const projectIdStale =
        selectedProjectId != null && pathIsUnderOrEqualFolder(selectedProjectId, deletedPath)

      if (remaining.length === openTabs.length && !projectIdStale) return

      if (remaining.length !== openTabs.length) {
        setOpenTabs(remaining)
      }

      if (projectIdStale) {
        setSelectedProjectId(parentContentFolderPath(deletedPath))
      }

      if (!activeWasRemoved && !projectIdStale) return

      markProgrammaticNavigation()

      if (remaining.length > 0) {
        const fallback =
          remaining.find((t) => t.id === 'home' || t.type === 'home') ??
          remaining[remaining.length - 1]
        setActiveTabId(fallback.id)
        navigate(urlForOpenTab(fallback, workspacesRoute), { replace: true })
        return
      }

      const homeTab: OpenTab = {
        id: 'home',
        title: 'Home',
        type: 'home',
        isDirty: false,
        tabSlotId: 'tab-main',
      }
      setOpenTabs([homeTab])
      navigate('/', { replace: true })
      setActiveTabId('home')
    }

    window.addEventListener('folder-deleted', handler as EventListener)
    return () => window.removeEventListener('folder-deleted', handler as EventListener)
  }, [
    openTabs,
    activeTabId,
    selectedProjectId,
    setOpenTabs,
    setActiveTabId,
    setSelectedProjectId,
    navigate,
    workspacesRoute,
  ])
}
